import { Checkbox, FormLabel, OptionType, SearchableSelect, Textinput } from "shared";
import { ComponentTypes, ITemplateComponent, IVisibilityTemplateComponentProps } from "entities/template";
import React, { useEffect, useMemo, useState } from "react";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { TagSelectField } from "../tag_select_field/tag_select_field";
import styles from "./visibility_field.module.scss";

interface IVisibilityFieldProps {
    component: ITemplateComponent;
    componentList: ITemplateComponent[];
    onChange: (value: IVisibilityTemplateComponentProps) => void;

}

const valueToString = (val?: string | boolean): string => {
    if (typeof val === "boolean") {
        return val ? "true" : "false";
    } else {
        return val ? val : "";
    }
};

const transformBoolean = (val: string) => {
    switch (val) {
        case "true":
            return true;
        case "false":
            return false;
        default:
            return val;
    }
};

export const VisibilityField: React.FC<IVisibilityFieldProps> = React.memo(({
                                                                                component,
                                                                                componentList,
                                                                                onChange
                                                                            }) => {
    const t = useComponentsTranslations();
    const [selectedComponentId, setSelectedComponentId] = useState<number>();
    const [visibilityParentName, setVisibilityParentName] = useState<string | undefined>(component.visibilityParentName ? component.visibilityParentName : undefined);
    const [visibilityParentNegate, setVisibilityParentNegate] = useState<boolean>(!!component.visibilityParentNegate);
    const [visibilityParentValue, setVisibilityParentValue] = useState<string>(valueToString(component.visibilityParentValue));

    const selectTypes = useMemo(() => [ComponentTypes.table, ComponentTypes.radio, ComponentTypes.checkbox, ComponentTypes.select, ComponentTypes.inputWithSelect], []);

    const showSelect = useMemo((): boolean => {
        if (typeof selectedComponentId !== "number") {
            return false;
        }
        const c = componentList.find((c) => c.id === selectedComponentId);
        if (!c) {
            return false;
        } else {
            return selectTypes.includes(c.type);
        }
    }, [selectedComponentId, componentList]);

    const selectOptions = useMemo((): OptionType[] => {
        if (typeof selectedComponentId !== "number") {
            return [];
        }
        const comp = componentList.find((c) => c.id === selectedComponentId);
        if (!comp || !selectTypes.includes(comp.type)) {
            return [];
        } else {
            if (comp.type !== ComponentTypes.checkbox) {
                // @ts-ignore
                const listValue = comp.list as string[] | undefined;
                return (listValue && Array.isArray(listValue)) ? listValue.map((value) => {
                    return {
                        label: value,
                        value: value
                    };
                }) : [];
            } else {
                return [{
                    label: t("yes"),
                    value: "true"
                }, {
                    label: t("no"),
                    value: "false"
                }];
            }
        }
    }, [selectedComponentId, componentList]);

    const isFileComponent = useMemo(() => {
        const component = componentList.find(c => c.id === selectedComponentId);
        return component?.type === ComponentTypes.file;
    }, [selectedComponentId]);

    useEffect(() => {
        onChange({
            visibilityParentName: visibilityParentName,
            visibilityParentNegate: !!visibilityParentName ? visibilityParentNegate : undefined,
            visibilityParentValue: !!visibilityParentName ? transformBoolean(visibilityParentValue) : undefined,
        });
    }, [visibilityParentName, visibilityParentNegate, visibilityParentValue]);

    useEffect(() => {
        // @ts-ignore
        const tagComponent = component.visibilityParentName ? componentList.find((c) => c?.name === component.visibilityParentName) : undefined;
        setVisibilityParentName(component.visibilityParentName ? component.visibilityParentName : undefined);
        setVisibilityParentNegate(!!component.visibilityParentNegate);
        setVisibilityParentValue(valueToString(component.visibilityParentValue));
        setSelectedComponentId(tagComponent ? tagComponent.id as number : undefined);
    }, [component.id]);

    useEffect(() => {
        if (typeof selectedComponentId === "number") {
            const selectedComponent = componentList.find((c) => c.id === selectedComponentId);
            if (selectedComponent && selectedComponent.type === ComponentTypes.checkbox && !visibilityParentValue) {
                setVisibilityParentValue("false");
            }
        }
    }, [selectedComponentId]);

    return <div>
        <TagSelectField
            title={t("visibilitySelect")}
            id="component_field_visibility_select"
            value={visibilityParentName}
            componentList={componentList}
            currentComponent={component}
            onChange={(val, id) => {
                setVisibilityParentName(val);
                setVisibilityParentValue("");
                setVisibilityParentNegate(false);
                setSelectedComponentId(id);
            }}/>
        {visibilityParentName && <Checkbox
            label={isFileComponent ? t("notExists") : t("notEquals")}
            id="component_field_visibility_exists"
            className={styles.checkbox}
            size="m"
            onChange={() => setVisibilityParentNegate((state) => !state)}
            checked={visibilityParentNegate}
        />}
        {visibilityParentName && !isFileComponent && <FormLabel title={t("visibilitySelectValue")}>
            {!showSelect && <Textinput size="m"
                                       id="component_field_visibility_text_value"
                                       value={visibilityParentValue}
                                       onChange={(e) => setVisibilityParentValue(e.target.value)}
                                       view="outline"/>}
            {showSelect && <SearchableSelect options={selectOptions}
                                             id="component_field_visibility_select_value"
                                             optionValue={selectOptions.find((o) => o.value === visibilityParentValue) as OptionType}
                                             onChange={(value) => setVisibilityParentValue(value ? value as string : "")}
                                             isMulti={false}
                                             hideClearIcon={false}
                                             isSearchable={true}/>}
        </FormLabel>}
    </div>;

});
